import {
  type ComponentProps,
  type ComponentType,
  createElement,
  type LazyExoticComponent,
  lazy,
  Suspense,
} from "react";
import { RouteLoading } from "../ui/route-loading";

type NamedComponent<Module, Name extends keyof Module> =
  Module[Name] extends ComponentType<infer _Props> ? Module[Name] : never;

/**
 * Load one named React component without turning its whole feature module into startup work.
 *
 * Route modules deliberately export named components. Repeating `lazy(() => import(…).then())`
 * at every route both obscures the boundary and makes the cast needed for a named export somebody
 * else's problem each time. This is that one boundary: Vite keeps the dynamic import as a split
 * point, React caches both the import promise and the resolved component, and the caller retains
 * the component's exact props.
 */
export function lazyNamed<Module, Name extends keyof Module>(
  load: () => Promise<Module>,
  name: Name,
): LazyExoticComponent<NamedComponent<Module, Name>> {
  type Loaded = NamedComponent<Module, Name>;
  return lazy(async () => {
    const module = await load();
    return { default: module[name] as Loaded };
  });
}

/** Only route modules own a Page fallback; lazy create forms keep their pane's fallback. */
export function lazyScreen<Module, Name extends keyof Module>(
  load: () => Promise<Module>,
  name: Name,
) {
  type Props = ComponentProps<NamedComponent<Module, Name>> & object;
  const Component = lazyNamed(load, name);
  // The boundary belongs to this screen, not the whole router. A new screen mounts
  // its own fallback even inside RouterProvider's startTransition. A shared deferred
  // boundary would keep the old screen (and its portals) actionable during that wait.
  // This identity is created once beside the route declaration, never keyed by URL:
  // selecting another message still preserves the same mounted master/detail screen.
  return function NamedScreen(props: Props) {
    return createElement(
      Suspense,
      { fallback: createElement(RouteLoading) },
      createElement(Component as ComponentType<Props>, props),
    );
  };
}
